// src/components/tableros/Campo/Tablas/HorizontalEventTable.js
import React, { useEffect, useRef } from 'react';

const HorizontalEventTable = ({
  eventData = {
    eventName: 'No hay eventos seleccionados',
    eventSubtitle: '',
    participants: [],
    activeAthlete: null,
    displayMode: 'multiple'
  },

  displayRows = 1,
  tableWidth = 1000,
  tableHeight = 800,
  
  // Colores
  tableBackgroundColor = '#1a1a1a',
  headerColor = '#2c0369',           
  positionColor = '#4c516d',
  positionBackgroundColor = '#4c516d',
  nameBackgroundColor = '#515e7b',
  markBackgroundColor = '#000000',   
  attemptBackgroundColor = '#0d47a1',
  headerFontColor = '#ffffff',
  athleteNameColor = '#ffffff',
  teamNameColor = '#e0e0e0',
  resultColor = '#ffffff',           
  attemptColor = '#ffffff',
  laneColor = '#ffffff',
  numberColor = '#ffffff',              
  tableRowOddColor = '#252525',   
  tableRowEvenColor = '#1e1e1e',
  columnsHeaderColor = '#4c516d',

  // Tipografía
  headerFontSize = 24,
  positionFontSize = 48,
  nameFontSize = 36,
  teamFontSize = 24,
  resultFontSize = 54,              
  attemptFontSize = 18,
  
  // Familias de fuentes
  headerFontFamily = 'Roboto',
  nameFontFamily = 'Roboto',
  numbersFontFamily = 'Roboto Mono',

  showAttemptNumber = true,
  showLane = true,
  showNumber = false,
}) => {
  const listContainerRef = useRef(null);
  
  const formatMark = (mark) => {
    const meters = typeof mark === 'string' ? parseFloat(mark) : mark;
    if (isNaN(meters)) return mark;
    return `${meters.toFixed(2)} m`;
  };

  const formatWind = (wind) => {
    const w = typeof wind === 'string' ? parseFloat(wind) : wind;
    if (w == null || isNaN(w)) return null;
    const sign = w >= 0 ? '+' : '';
    return `${sign}${w.toFixed(1)} m/s`;
  };

  // ✅ FUNCIÓN LIMPIA: Determinar estado del tablero
  const getTableroState = () => {
    // Solo aplicar estados dinámicos en vista individual (displayRows === 1)
    if (displayRows !== 1) {
      return 'multiple';
    }

    // Estado 1: Sin atleta activo
    if (!eventData.activeAthlete || !eventData.activeAthlete.isActive) {
      return 'initial';
    }
    
    // Estado 2: Atleta activo pero sin resultado en el intento actual
    const hasCurrentResult = eventData.activeAthlete.currentResult && 
                             eventData.activeAthlete.currentResult !== '-' && 
                             eventData.activeAthlete.currentResult.trim() !== '';
    
    if (!hasCurrentResult) {
      return 'athlete-selected';
    }
    
    // Estado 3: Atleta activo con resultado
    return 'with-result';
  };

  const tableroState = getTableroState();

  // Prepara el array de atletas a mostrar
  const athletes = displayRows === 1
    ? [
        eventData.activeAthlete && eventData.activeAthlete.isActive
          ? eventData.activeAthlete
          : (eventData.participants[0] || {})
      ]
    : eventData.participants.slice(0, displayRows);
  
  // Reset de scroll al cambiar filas
  useEffect(() => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
  }, [displayRows]);

  // ✅ ESTADO 1: INICIAL - Solo nombre del evento y logo
  if (displayRows === 1 && tableroState === 'initial') {
    return (
      <div
        style={{
          position: 'relative',
          width: `${tableWidth}px`, 
          height: `${tableHeight}px`,  
          backgroundColor: tableBackgroundColor,
          color: headerFontColor,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header del evento */}
        <div 
          style={{ 
            width: '100%', 
            height: '20%',
            backgroundColor: headerColor, 
            padding: '20px', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center' 
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div 
              style={{ 
                fontWeight: 'bold', 
                fontSize: `${headerFontSize * 1.8}px`, 
                color: headerFontColor,
                fontFamily: headerFontFamily
              }}
            >
              {eventData.eventName}
            </div>
            {eventData.eventSubtitle && (
              <div 
                style={{ 
                  fontSize: `${headerFontSize}px`, 
                  color: headerFontColor, 
                  opacity: 0.8, 
                  marginTop: '10px',
                  fontFamily: headerFontFamily
                }}
              >
                {eventData.eventSubtitle}
              </div>
            )}
          </div>
        </div>

        {/* Contenido principal - Logo del evento */}
        <div 
          style={{ 
            flex: 1,
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '40px'
          }}
        >
          {/* Logo del evento */}
          <div 
            style={{ 
              fontSize: `${headerFontSize * 6}px`, 
              fontWeight: 'bold', 
              color: headerFontColor,
              fontFamily: headerFontFamily,
              textAlign: 'center',
              opacity: 0.7
            }}
          >
            🏃‍♂️
          </div>
        </div>
      </div>
    );
  }

  // ✅ ESTADO 2: ATLETA SELECCIONADO SIN RESULTADO - Nombre completo + intento
  if (displayRows === 1 && tableroState === 'athlete-selected') {
    const athlete = athletes[0] || {};
    const currentAttemptNumber = athlete.activeAttempt || athlete.currentAttempt || athlete.attemptNumber || '1';

    return (
      <div
        style={{
          position: 'relative',
          width: `${tableWidth}px`, 
          height: `${tableHeight}px`,  
          backgroundColor: tableBackgroundColor,
          color: headerFontColor,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header del evento */}
        <div 
          style={{ 
            width: '100%', 
            height: '15%',
            backgroundColor: headerColor, 
            padding: '15px', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center' 
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div 
              style={{ 
                fontWeight: 'bold', 
                fontSize: `${headerFontSize * 1.2}px`, 
                color: headerFontColor,
                fontFamily: headerFontFamily
              }}
            >
              {eventData.eventName}
            </div>
            {eventData.eventSubtitle && (
              <div 
                style={{ 
                  fontSize: `${headerFontSize * 0.7}px`, 
                  color: headerFontColor, 
                  opacity: 0.8,
                  marginTop: '5px',
                  fontFamily: headerFontFamily
                }}
              >
                {eventData.eventSubtitle}
              </div>
            )}
          </div>
        </div>

        {/* Contenido principal */}
        <div 
          style={{ 
            flex: 1,
            display: 'flex', 
            flexDirection: 'column' 
          }}
        >
          {/* Mitad superior: NOMBRE EXPANDIDO - Ocupa TODO el espacio de posición + nombre */}
          <div 
            style={{ 
              height: '50%',
              width: '100%',
              backgroundColor: nameBackgroundColor, 
              padding: '30px', 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <div 
              style={{ 
                fontSize: `${nameFontSize}px`, 
                fontWeight: 'bold', 
                color: athleteNameColor, 
                lineHeight: 1.1,
                fontFamily: nameFontFamily,
                marginBottom: '20px'
              }}
            >
              {athlete.name || athlete.fullName || 'Sin nombre'}
            </div>
            <div 
              style={{ 
                fontSize: `${teamFontSize}px`, 
                color: teamNameColor, 
                lineHeight: 1.2,
                fontFamily: nameFontFamily
              }}
            >
              {athlete.team || athlete.affiliation || 'Sin equipo'}
            </div>
          </div>

          {/* Mitad inferior: INTENTO EXPANDIDO - Ocupa TODO el espacio de intento + marca */}
          <div 
            style={{ 
              height: '50%',
              width: '100%',
              backgroundColor: attemptBackgroundColor,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '20px',
              gap: '20px'
            }}
          >
            <div 
              style={{ 
                fontSize: `${attemptFontSize * 1.5}px`, 
                fontWeight: 'bold', 
                color: attemptColor,
                fontFamily: nameFontFamily
              }}
            >
              INTENTO
            </div>
            <div 
              style={{ 
                fontSize: `${resultFontSize * 1.2}px`, 
                fontWeight: 'bold', 
                color: attemptColor,
                fontFamily: numbersFontFamily
              }}
            >
              {currentAttemptNumber}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ ESTADO 3: CON RESULTADO - Layout completo normal con posición
  if (displayRows === 1 && tableroState === 'with-result') {
    const athlete = athletes[0] || {};
    const athleteData = { ...athlete, ...eventData.activeAthlete };
    
    // Usar resultado actual si está disponible
    let displayMark = athleteData.currentResult || athleteData.mark;
    let displayWind = athleteData.currentWind || athleteData.wind;
    
    const rawMark = typeof displayMark === 'string' ? parseFloat(displayMark) : displayMark;
    const formattedMark = formatMark(rawMark);
    const formattedWind = displayWind != null ? formatWind(displayWind) : null;
    const currentAttemptNumber = athleteData.activeAttempt || athleteData.currentAttempt || athleteData.attemptNumber || '1';

    // Proporciones normales para layout completo
    const headerHeight = '12%';
    const contentHeight = '88%';
    const topSectionHeight = '50%';
    const bottomSectionHeight = '50%';
    const topPositionWidth = '20%';
    const topNameWidth = '80%';
    const bottomAttemptWidth = showAttemptNumber ? '25%' : '0%';
    const bottomMarkWidth = showAttemptNumber ? '75%' : '100%';

    return (
      <div
        style={{
          position: 'relative',
          width: `${tableWidth}px`, 
          height: `${tableHeight}px`,  
          backgroundColor: tableBackgroundColor,
          color: headerFontColor,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <div 
          style={{ 
            width: '100%', 
            height: headerHeight,
            backgroundColor: headerColor, 
            padding: '10px', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center' 
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div 
              style={{ 
                fontWeight: 'bold', 
                fontSize: `${headerFontSize}px`, 
                color: headerFontColor,
                fontFamily: headerFontFamily
              }}
            >
              {eventData.eventName}
            </div>
            {eventData.eventSubtitle && (
              <div 
                style={{ 
                  fontSize: `${headerFontSize * 0.6}px`, 
                  color: headerFontColor, 
                  opacity: 0.8,
                  marginTop: '5px',
                  fontFamily: headerFontFamily
                }}
              >
                {eventData.eventSubtitle}
              </div>
            )}
          </div>
        </div>

        {/* Contenido principal */}
        <div 
          style={{ 
            flex: 1, 
            height: contentHeight,
            display: 'flex', 
            flexDirection: 'column' 
          }}
        >
          {/* Mitad superior: posición, nombre y equipo */}
          <div 
            style={{ 
              display: 'flex', 
              height: topSectionHeight 
            }}
          >
            {/* Posición */}
            <div 
              style={{ 
                width: topPositionWidth, 
                backgroundColor: positionBackgroundColor, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center' 
              }}
            >
              <div 
                style={{ 
                  fontSize: `${positionFontSize}px`, 
                  fontWeight: 'bold', 
                  color: headerFontColor,
                  fontFamily: numbersFontFamily
                }}
              >
                {athleteData.position || athleteData.place || '-'}
              </div>
            </div>
            {/* Nombre y equipo */}
            <div 
              style={{ 
                width: topNameWidth, 
                backgroundColor: nameBackgroundColor, 
                padding: '16px', 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center' 
              }}
            >
              <div 
                style={{ 
                  fontSize: `${nameFontSize}px`, 
                  fontWeight: 'bold', 
                  color: athleteNameColor, 
                  lineHeight: 1.2,
                  fontFamily: nameFontFamily
                }}
              >
                {athleteData.name || athleteData.fullName || 'Sin nombre'}
              </div>
              <div 
                style={{ 
                  fontSize: `${teamFontSize}px`, 
                  color: teamNameColor, 
                  lineHeight: 1.2,
                  fontFamily: nameFontFamily,
                  marginTop: '8px'
                }}
              >
                {athleteData.team || athleteData.affiliation || 'Sin equipo'}
              </div>
            </div>
          </div>

          {/* Mitad inferior: intento y marca */}
          <div 
            style={{ 
              display: 'flex', 
              height: bottomSectionHeight 
            }}
          >
            {/* Intento */}
            {showAttemptNumber && (
              <div 
                style={{
                  width: bottomAttemptWidth,
                  backgroundColor: attemptBackgroundColor,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '10px 0'
                }}
              >
                <div 
                  style={{ 
                    fontSize: `${attemptFontSize}px`, 
                    fontWeight: 'bold', 
                    color: attemptColor,
                    fontFamily: nameFontFamily
                  }}
                >
                  Intento
                </div>
                <div 
                  style={{ 
                    fontSize: `${attemptFontSize}px`, 
                    fontWeight: 'bold', 
                    color: attemptColor, 
                    marginTop: '5px',
                    fontFamily: numbersFontFamily
                  }}
                >
                  {currentAttemptNumber}
                </div>
              </div>
            )}
            {/* Marca y viento */}
            <div 
              style={{ 
                width: bottomMarkWidth, 
                backgroundColor: markBackgroundColor, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center', 
                alignItems: 'center', 
                padding: '16px'
              }}
            >
              <div 
                style={{ 
                  fontSize: `${resultFontSize}px`, 
                  fontWeight: 'bold', 
                  color: resultColor, 
                  fontFamily: numbersFontFamily
                }}
              >
                {formattedMark}
              </div>
              {formattedWind && (
                <div 
                  style={{ 
                    fontSize: `${resultFontSize * 0.4}px`, 
                    color: resultColor, 
                    opacity: 0.8,
                    marginTop: '10px',
                    fontFamily: numbersFontFamily
                  }}
                >
                  {formattedWind}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ VISTA MÚLTIPLE O ESTADO NORMAL - Mantener código existente
  const hasValidResults = () => {
    return athletes.some(athlete => 
      athlete.position && 
      athlete.position !== '' && 
      athlete.position !== undefined && 
      athlete.position !== null
    );
  };

  const shouldDisplayLaneColumn = () => {
    if (displayRows === 1) return showLane;
    const hasResults = hasValidResults();
    return !hasResults || (hasResults && showLane);
  };

  const shouldDisplayAttemptNumber = () => {
    if (displayRows === 1) return showAttemptNumber;
    const hasResults = hasValidResults();
    return !hasResults || (hasResults && showAttemptNumber);
  };

  const calculateRowHeight = () => {
    const availableHeight = tableHeight - 150;
    return availableHeight / displayRows;
  };

  const displayLaneColumn = shouldDisplayLaneColumn();
  const displayAttemptNumber = shouldDisplayAttemptNumber();

  return (
    <div
      style={{
        width: `${tableWidth}px`,
        height: `${tableHeight}px`,
        backgroundColor: tableBackgroundColor,
        color: headerFontColor,
        padding: '0',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Encabezado del evento */}
      <div
        style={{
          backgroundColor: headerColor,
          padding: '10px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: `${headerFontSize}px`,
            fontWeight: 'bold',
            color: headerFontColor,
            fontFamily: headerFontFamily
          }}
        >
          {eventData.eventName}
        </div>
        {eventData.eventSubtitle && (
          <div
            style={{
              fontSize: `${headerFontSize * 0.6}px`,
              color: headerFontColor,
              opacity: 0.8,
              marginTop: '5px',
              fontFamily: headerFontFamily
            }}
          >
            {eventData.eventSubtitle}
          </div>
        )}
      </div>

      {/* Aquí iría el resto del código para vista múltiple */}
      <div style={{ 
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: `${headerFontSize}px`,
        color: headerFontColor,
        opacity: 0.6
      }}>
        Vista múltiple ({displayRows} filas)
      </div>
    </div>
  );
};

export default HorizontalEventTable;